// page-resume-handler.ts
// Centralised, cancellable resume pipeline for SPA lifecycle recovery.
// Handles: tab resume, focus, pageshow, sleep detection, and manual triggers.
// Supports ordered stages, abortable hooks, and hard/soft cancellation.

//we need to implement this in our application initiailzation of dealer and supplier projects.
//we need to bind in and compliment our resource locking loop, authentication and error handling.
//do an authentication reverification test and reconnect before anything else.
//depending on token, check its expiry and if it can be auto-refreshed, do so. if not, then we need to cancel the resume and force a logout and redirect to login page.
//we can have a stage for realtime connections, so we can attempt to reconnect any sse or websocket connections early in the process to help ensure they are back up before we do any data calls that might depend on them.
//we can have a stage for locks, so we can recheck any critical locks we hold and if we lost them, we can cancel the resume with a workflow exit so the user doesnt continue in a broken state.
//then we can have stages for data refresh, ui refresh, etc. depending on how robust we want this to be and how much of our app needs to be refreshed on these lifecycle events.

//we need to ensure that this is checking the health of the user, sse connections, locks and otherwise in an appropriate order, and stopping cascading dialogs or erros etc.

export enum ResumeStage {
  Auth = 1,
  Realtime = 2,
  Locks = 3,
  Data = 4,
  UI = 5
}

export enum ResumeCancelType {
  Hard = 'hard', // logout / tenant invalid / fatal auth
  Workflow = 'workflow', // exit current editor/workflow only
  Soft = 'soft' // stop remaining hooks but keep app running
}

export enum ResumeResult {
  Completed = 'completed',
  Cancelled = 'cancelled',
  AuthFailed = 'auth-failed',
  WorkflowExited = 'workflow-exited',
  NetworkOffline = 'network-offline',
  Error = 'error'
}

export interface ResumeContext {
  abortSignal: AbortSignal;
  cancel: (type?: ResumeCancelType, reason?: string) => void;
  metadata: Record<string, unknown>;
}

export type ResumeHook = (ctx: ResumeContext) => Promise<void> | void;

export interface PageResumeHandlerOptions {
  sleepThresholdMs?: number; // how long timer drift indicates sleep
  driftCheckIntervalMs?: number;
  debug?: boolean;
}

type StageMap = Map<number, ResumeHook[]>;

export class PageResumeHandler {
  private stages: StageMap = new Map();
  private controller?: AbortController;
  private running = false;
  private lastTick = Date.now();
  private driftTimer?: number;
  private options: Required<PageResumeHandlerOptions>;

  // Optional callbacks for host app to respond to lifecycle events
  public onHardCancel?: (reason?: string) => void;
  public onWorkflowCancel?: (reason?: string) => void;
  public onResult?: (result: ResumeResult, reason?: string) => void;

  constructor(opts?: PageResumeHandlerOptions) {
    this.options = {
      sleepThresholdMs: opts?.sleepThresholdMs ?? 60000,
      driftCheckIntervalMs: opts?.driftCheckIntervalMs ?? 10000,
      debug: opts?.debug ?? false
    };
  }

  // Register a hook into a stage (ordered execution)
  register(stage: ResumeStage | number, hook: ResumeHook): void {
    if (!this.stages.has(stage)) this.stages.set(stage, []);
    this.stages.get(stage)!.push(hook);
  }

  // Start listening to browser lifecycle signals
  start(): void {
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('focus', this.handleResumeSignal);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('online', this.handleOnline);

    this.driftTimer = window.setInterval(this.detectSleepDrift, this.options.driftCheckIntervalMs);
  }

  // Stop listeners (e.g., during full teardown / logout)
  stop(): void {
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('focus', this.handleResumeSignal);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('online', this.handleOnline);

    if (this.driftTimer) {
      clearInterval(this.driftTimer);
      this.driftTimer = undefined;
    }
  }

  // External trigger (API 401/412, SSE reconnect, manual call, etc.)
  async trigger(metadata?: Record<string, unknown>): Promise<ResumeResult> {
    return this.runResume(metadata);
  }

  // Force cancel of current run (e.g., logout button)
  cancelCurrent(reason?: string): void {
    if (this.controller) {
      this.log('Manual cancel invoked', reason);
      this.controller.abort(reason ?? 'manual-cancel');
    }
  }

  // ---- Internal implementation ----

  private handleVisibility = (): void => {
    if (!document.hidden) this.handleResumeSignal();
  };

  private handlePageShow = (evt: PageTransitionEvent): void => {
    // bfcache restore or navigation back
    if (evt.persisted) {
      this.handleResumeSignal();
    }
  };

  private handleOnline = (): void => {
    this.log('Network online → triggering resume');
    this.handleResumeSignal();
  };

  private handleResumeSignal = (): void => {
    void this.runResume({ source: 'browser-signal' });
  };

  private detectSleepDrift = (): void => {
    const now = Date.now();
    const delta = now - this.lastTick;
    this.lastTick = now;

    if (delta > this.options.sleepThresholdMs) {
      this.log('Sleep drift detected → triggering resume', { delta });
      void this.runResume({ source: 'sleep-drift', delta });
    }
  };

  private async runResume(metadata?: Record<string, unknown>): Promise<ResumeResult> {
    if (this.running) {
      this.log('Resume already running → skipping');
      return ResumeResult.Cancelled;
    }

    this.running = true;
    this.controller = new AbortController();

    let result: ResumeResult = ResumeResult.Completed;
    let cancelType: ResumeCancelType | undefined;
    let cancelReason: string | undefined;

    const ctx: ResumeContext = {
      abortSignal: this.controller.signal,
      cancel: (type?: ResumeCancelType, reason?: string) => {
        cancelType = type ?? ResumeCancelType.Soft;
        cancelReason = reason;
        this.log('Resume cancelled', { cancelType, cancelReason });
        this.controller?.abort(reason ?? 'cancelled');
      },
      metadata: metadata ?? {}
    };

    try {
      const orderedStages = [...this.stages.keys()].sort((a, b) => a - b);

      for (const stage of orderedStages) {
        const hooks = this.stages.get(stage)!;
        this.log(`Running stage ${stage} (${hooks.length} hooks)`);

        for (const hook of hooks) {
          if (ctx.abortSignal.aborted) break;

          try {
            await hook(ctx);
          } catch (err) {
            this.log('Hook error', err);
            result = ResumeResult.Error;
            ctx.cancel(ResumeCancelType.Soft, 'hook-error');
            break;
          }
        }

        if (ctx.abortSignal.aborted) break;
      }

      if (ctx.abortSignal.aborted) {
        result =
          cancelType === ResumeCancelType.Hard
            ? ResumeResult.AuthFailed
            : cancelType === ResumeCancelType.Workflow
              ? ResumeResult.WorkflowExited
              : ResumeResult.Cancelled;

        // Notify host app for targeted teardown
        if (cancelType === ResumeCancelType.Hard && this.onHardCancel) {
          this.onHardCancel(cancelReason);
        } else if (cancelType === ResumeCancelType.Workflow && this.onWorkflowCancel) {
          this.onWorkflowCancel(cancelReason);
        }
      }

      return result;
    } finally {
      this.running = false;
      this.controller = undefined;

      this.onResult?.(result, cancelReason);
      this.log('Resume finished', { result, cancelReason });
    }
  }

  private log(message: string, data?: unknown): void {
    if (!this.options.debug) return;
    // eslint-disable-next-line no-console
    console.debug('[PageResumeHandler]', message, data ?? '');
  }
}

// ---- Singleton export (recommended usage) ----

export const pageResumeHandler = new PageResumeHandler({
  sleepThresholdMs: 60000,
  driftCheckIntervalMs: 10000,
  debug: false
});

/*
USAGE EXAMPLE:

import { pageResumeHandler, ResumeStage, ResumeCancelType } from './page-resume-handler';

// Start listening once app boots
pageResumeHandler.start();

// AUTH
pageResumeHandler.register(ResumeStage.Auth, async (ctx) => {
  const ok = await authService.revalidateSession();
  if (!ok) {
    ctx.cancel(ResumeCancelType.Hard, 'Session expired');
    authService.logout();
  }
});

// REALTIME
pageResumeHandler.register(ResumeStage.Realtime, async () => {
  await sseService.reconnectIfNeeded();
});

// LOCKS
pageResumeHandler.register(ResumeStage.Locks, async (ctx) => {
  const valid = await lockService.recheckLocks();
  if (!valid) {
    ctx.cancel(ResumeCancelType.Workflow, 'Lost lock ownership');
    workflowService.exitEditor();
  }
});

// DATA
pageResumeHandler.register(ResumeStage.Data, async () => {
  await dataSyncService.refreshCriticalState();
});

// Trigger from API 412/401 interceptors:
apiClient.onPreconditionFailed(() => pageResumeHandler.trigger({ source: '412' }));
apiClient.onUnauthorized(() => pageResumeHandler.trigger({ source: '401' }));
*/
